import { useRef, useCallback } from 'react';

export interface FocalPoint { x: number; y: number }

export const focalPos = (fp?: FocalPoint) =>
  `${((fp?.x ?? 0.5) * 100).toFixed(1)}% ${((fp?.y ?? 0.5) * 100).toFixed(1)}%`;

interface FocalPointPickerProps {
  imageUrl: string;
  value?: FocalPoint;
  onChange: (point: FocalPoint | undefined) => void;
  aspectRatio?: string;
}

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

export function FocalPointPicker({ imageUrl, value, onChange, aspectRatio = '16 / 9' }: FocalPointPickerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);

  const point = value ?? { x: 0.5, y: 0.5 };

  const updateFromEvent = useCallback((clientX: number, clientY: number) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    onChange({
      x: clamp01((clientX - rect.left) / rect.width),
      y: clamp01((clientY - rect.top) / rect.height),
    });
  }, [onChange]);

  const onPointerDown = (e: React.PointerEvent) => {
    draggingRef.current = true;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    updateFromEvent(e.clientX, e.clientY);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    updateFromEvent(e.clientX, e.clientY);
  };
  const onPointerUp = (e: React.PointerEvent) => {
    draggingRef.current = false;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
  };

  return (
    <div className="space-y-2">
      <div
        ref={ref}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        className="relative w-full rounded-lg overflow-hidden bg-gray-100 cursor-crosshair select-none touch-none"
        style={{ aspectRatio }}
      >
        <img
          src={imageUrl}
          alt=""
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          style={{ objectPosition: `${point.x * 100}% ${point.y * 100}%` }}
        />
        <div
          className="absolute w-5 h-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-lg pointer-events-none"
          style={{
            left: `${point.x * 100}%`,
            top: `${point.y * 100}%`,
            backgroundColor: 'color-mix(in srgb, var(--accent, #D6451F) 90%, transparent)',
          }}
        />
      </div>
      <div className="flex items-center justify-between text-xs" style={{ color: 'var(--ink-3, #6b7280)' }}>
        <span>Drag to set focal point</span>
        {value && (
          <button
            type="button"
            onClick={() => onChange(undefined)}
            className="font-medium"
            style={{ color: 'var(--accent, #2563eb)' }}
          >
            Reset to centre
          </button>
        )}
      </div>
    </div>
  );
}
